import { useMemo } from 'react';
import useAbortRegistry from '@dr-pam/common-components/Hooks/useAbortRegistry';
import FetchUtils from '@dr-pam/common-components/Utils/FetchUtils';
import type { RegisterAbortFunction } from '@dr-pam/common-components/Utils/AbortUtils';
import type {
	ProductPurchasesReportParams,
	SubscriptionsReportParams,
	CouponReportParams,
	HealthProfessionalReportParams,
} from '@dr-pam/common-components/Services/Server/ReportService';

export default class ReportService {
	constructor(private readonly _registerAbort?: RegisterAbortFunction) {}

	public async getSubscriptionsReport(params: SubscriptionsReportParams): Promise<Blob> {
		const request = FetchUtils.abortableRequest(
			FetchUtils.post<SubscriptionsReportParams>('/api/report/subscriptions', params),
			this._registerAbort,
		);

		const response = await request.response;

		if (!response.ok) {
			throw new Error(`Failed to fetch subscriptions report: ${response.statusText}`);
		}

		return response.blob();
	}

	public async getProductPurchasesReport(params: ProductPurchasesReportParams): Promise<Blob> {
		const request = FetchUtils.abortableRequest(
			FetchUtils.post<ProductPurchasesReportParams>('/api/report/product-purchases', params),
			this._registerAbort,
		);

		const response = await request.response;

		if (!response.ok) {
			throw new Error(`Failed to fetch subscriptions report: ${response.statusText}`);
		}

		return response.blob();
	}

	public async getCouponReport(params: CouponReportParams): Promise<Blob> {
		const request = FetchUtils.abortableRequest(
			FetchUtils.post<CouponReportParams>('/api/report/coupons', params),
			this._registerAbort,
		);

		const response = await request.response;

		if (!response.ok) {
			throw new Error(`Failed to fetch coupon report: ${response.statusText}`);
		}

		return response.blob();
	}

	public async getHealthProfessionalReport(params: HealthProfessionalReportParams): Promise<Blob> {
		const request = FetchUtils.abortableRequest(
			FetchUtils.post<HealthProfessionalReportParams>('/api/report/health-professional', params),
			this._registerAbort,
		);

		const response = await request.response;

		if (!response.ok) {
			throw new Error(`Failed to fetch health professional report: ${response.statusText}`);
		}

		return response.blob();
	}
}

export function useReportService() {
	const registerAbort = useAbortRegistry();

	return useMemo(() => new ReportService(registerAbort), [registerAbort]);
}
